import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Button, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Radio, RadioGroup, TextField, Typography, useTheme } from '@mui/material';
import * as fns from 'date-fns';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import MaskedInput from 'react-text-mask';
import * as Yup from 'yup';
import AttachmentTaskType from '../../enum/AttachmentTaskType';
import TextTemplateType from '../../enum/TextTemplateType';
import UserType from '../../enum/UserType';
import AttachmentTask from '../../model/AttachmentTask';
import SystemAttachment from '../../model/SystemAttachment';
import Task from '../../model/Task';
import TextTemplate from '../../model/TextTemplate';
import User from '../../model/User';
import { TicketLayoutTypeRule } from '../../pages/System/Config/TicketLayoutTypes/TicketLayoutTypeForm';
import { isSameDay } from '../../pages/System/Task/Tasks/TaskEdit';
import { getUserWithVacationName } from '../../pages/System/Task/Tasks/TaskForm';
import { useAuthentication } from '../../reducer/authentication';
import { ServiceApi, WhereCondition } from '../../service/ServiceApi';
import { convertFileByteToSizeString, getUserDepartmentPermissions } from '../../utils';
import AttachmentPreviewBox from '../AttachmentPreviewBox';
import confirmation from '../ConfirmationDialog';
import DatePicker from '../DatePicker';
import DefaultButton from '../FormControl/DefaultButton';
import ErrorButton from '../FormControl/ErrorButton';
import { FormValidationSchema, validateSchema } from '../FormControl/Form/Form';
import FormDialog from '../FormControl/FormDialog';
import SafeDeskTextEditor from '../SafeDeskTextEditor';
import SelectSearch from '../SelectSearch';
import OptionItem from '../SelectSearch/OptionItem';
import SelectSearchMultiAsync from '../SelectSearch/SelectSearchMultiAsync';
import TextTemplateButtons from '../TextTemplateButtons';
import TimePicker from '../TimePicker';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
let activeCommentDialogs = 0;

export interface CommentFormDialogData {
    comment: string;
    isPublic: boolean | null;
    time: string;
    assigned_users: number[];
    assigned_users_options?: OptionItem<number>[];
    attachments: AttachmentTask[];
    transfer_task: boolean;
    transfer_task_user_id: number | null;
    add_reminder: boolean;
    reminder_date: Date | null;
    reminder_time: string;
    prod_amount: number | null;
    prod_amount_problem: number | null;
    send_comment_to_approver: boolean;
    total_time_development: number | null;
    estimated_delivery_after_approval: number | null;
    notify_others_users: number[];
    notify_others_users_options?: OptionItem<number>[];
    notify_approver: boolean;
    reply_status: CommentReplyStatus;
}

export type CommentReplyStatus = 'keepUnresolved' | 'markAwared' | 'markResolved';

export interface CommentReplyContext {
    recipients: User[];
    commentTaskUserId?: number;
    isAwared: boolean;
    isResolved: boolean;
}

export interface CommentFormDialogProps {
    commentDialogOpen: boolean;
    onSubmit: (comment: string, isPublic: boolean, time?: string, assigned_users?: number[], attachments?: AttachmentTask[], transfer_task?: boolean, transfer_task_user_id?: number | null,
        reminder_date?: Date | null, prod_amount?: number | null, prod_amount_problem?: number | null, send_comment_to_approver?: boolean, notify_others_users?: number[], notify_approver?: boolean,
        replyStatus?: CommentReplyStatus) => any;
    onCancel: () => any;
    allowPublicComment?: boolean;
    useTime?: boolean;
    useAssignedToUser?: boolean;
    enableAttachments?: boolean;
    enablePasteAttachments?: boolean;
    attachmentType?: AttachmentTaskType;
    onlyPublicComments?: boolean;
    showTextTemplates?: boolean;
    isInternal?: boolean;
    task?: Task;
    relatedUsers?: User[];
    allowSelfAssign?: boolean;
    enableReminder?: boolean;
    page?: 'task' | 'taskInternal' | 'taskInternalProductEngineering' | 'taskInternalSupport';
    showUserVacation?: boolean;
    ruleProdAmount?: TicketLayoutTypeRule;
    ticketTypeApprovers?: User[];
    enableSystemAttachments?: boolean;
    replyContext?: CommentReplyContext | null;
}

export default function CommentFormDialog(props: CommentFormDialogProps) {
    const { commentDialogOpen, onCancel, onSubmit, allowPublicComment, useTime, useAssignedToUser,
        enableAttachments, enablePasteAttachments, attachmentType, onlyPublicComments, showTextTemplates, isInternal, task,
        relatedUsers, allowSelfAssign, enableReminder, page, showUserVacation, ruleProdAmount, ticketTypeApprovers, enableSystemAttachments, replyContext } = props;

    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthentication();
    const theme = useTheme();
    const isDark2 = theme.palette.background.default === '#202326';
    const [userList, setUserList] = useState<OptionItem<number>[]>([]);
    const [loadedUser, setLoadedUser] = useState<User | null>(null);
    const apiUser = new ServiceApi<User>('user');
    const apiSystemAttachment = new ServiceApi<SystemAttachment>('systemAttachment');
    const [systemAttachments, setSystemAttachments] = useState<SystemAttachment[]>([]);
    const [systemAttachmentDialogOpen, setSystemAttachmentDialogOpen] = useState(false);
    const [systemAttachmentSetFieldValue, setSystemAttachmentSetFieldValue] = useState<any>(null);
    const [systemAttachmentCurrentAttachments, setSystemAttachmentCurrentAttachments] = useState<AttachmentTask[]>([]);
    const [systemAttachmentSearch, setSystemAttachmentSearch] = useState('');
    const systemAttachmentSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pasteSetFieldValueRef = useRef<((field: keyof CommentFormDialogData, value: any) => Promise<unknown>) | null>(null);
    const pasteAttachmentsRef = useRef<AttachmentTask[]>([]);
    const paperRef = useRef<HTMLDivElement | null>(null);
    const dragSnapshotRef = useRef({ offsetX: 0, offsetY: 0, width: 0, height: 0 });
    const draggingRef = useRef(false);
    const activeInstanceRef = useRef(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [dialogPosition, setDialogPosition] = useState(() => {
        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
        return {
            top: 80,
            left: Math.max((viewportWidth - 720) / 2, 16)
        };
    });

    useEffect(() => {
        if (user) {
            apiUser.getById(user.id, { relations: ['current_or_next_vacation', 'department', 'departments'] }).then(setLoadedUser);
        }
        onSearchUser('').then(setUserList);
    }, []);

    useEffect(() => {
        if (commentDialogOpen && !activeInstanceRef.current) {
            if (activeCommentDialogs > 0) {
                enqueueSnackbar('Já existe um comentário em edição. Finalize ou feche o atual antes de abrir outro.', { variant: 'info' });
                onCancel();
                return;
            }
            activeCommentDialogs += 1;
            activeInstanceRef.current = true;
            setIsMinimized(false);
        }

        if (!commentDialogOpen && activeInstanceRef.current) {
            activeCommentDialogs = Math.max(activeCommentDialogs - 1, 0);
            activeInstanceRef.current = false;
            setIsMinimized(false);
        }

        return () => {
            if (activeInstanceRef.current) {
                activeCommentDialogs = Math.max(activeCommentDialogs - 1, 0);
                activeInstanceRef.current = false;
            }
        };
    }, [commentDialogOpen, onCancel]);

    useEffect(() => {
        if (!commentDialogOpen) {
            return;
        }
        const recenter = () => {
            const width = paperRef.current?.offsetWidth || 720;
            const height = paperRef.current?.offsetHeight || 520;
            const left = Math.max((window.innerWidth - width) / 2, 16);
            const top = Math.max((window.innerHeight - height) / 5, 32);
            setDialogPosition({ left, top });
        };
        requestAnimationFrame(recenter);
    }, [commentDialogOpen]);

    useEffect(() => {
        if (!commentDialogOpen) {
            return;
        }
        const handleResize = () => {
            const width = paperRef.current?.offsetWidth || 720;
            const height = paperRef.current?.offsetHeight || 520;
            const maxLeft = Math.max(window.innerWidth - width - 16, 0);
            const maxTop = Math.max(window.innerHeight - (isMinimized ? 80 : height) - 16, 0);
            setDialogPosition((prev) => ({
                left: clamp(prev.left, 8, maxLeft),
                top: clamp(prev.top, 8, maxTop)
            }));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [commentDialogOpen, isMinimized]);

    const handleDragMove = useCallback((event: MouseEvent) => {
        if (!draggingRef.current) return;
        event.preventDefault();
        const { offsetX, offsetY, width, height } = dragSnapshotRef.current;
        const maxLeft = Math.max(window.innerWidth - width - 16, 0);
        const maxTop = Math.max(window.innerHeight - (isMinimized ? 80 : height) - 16, 0);
        const nextLeft = clamp(event.clientX - offsetX, 8, maxLeft);
        const nextTop = clamp(event.clientY - offsetY, 8, maxTop);
        setDialogPosition({ left: nextLeft, top: nextTop });
    }, [isMinimized]);

    const handleDragEnd = useCallback(() => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        setIsDragging(false);
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
    }, [handleDragMove]);

    const handleDragStart = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!commentDialogOpen) return;
        const paper = paperRef.current;
        if (!paper) return;
        const rect = paper.getBoundingClientRect();
        dragSnapshotRef.current = {
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            height: rect.height
        };
        draggingRef.current = true;
        setIsDragging(true);
        window.addEventListener('mousemove', handleDragMove);
        window.addEventListener('mouseup', handleDragEnd);
        event.preventDefault();
    };

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);
        };
    }, [handleDragEnd, handleDragMove]);

    function onSubmitHandler(data: CommentFormDialogData) {
        var reminderDate: Date | null = null;
        if (data.add_reminder && data.reminder_date && data.reminder_time) {

            const horario = data.reminder_time.split(':');

            //valida se horario é valido
            if (horario.length != 2 || !fns.isValid(fns.set(data.reminder_date, {
                hours: parseInt(horario[0]),
                minutes: parseInt(horario[1]),
                seconds: 0,
                milliseconds: 0
            }))) {
                enqueueSnackbar('Horário do lembrete inválido', { variant: 'error' });
                return;
            }

            reminderDate = fns.set(data.reminder_date, {
                hours: parseInt(horario[0]),
                minutes: parseInt(horario[1]),
                seconds: 0,
                milliseconds: 0
            });
        }

        if (data.send_comment_to_approver) {
            //console.log('data.total_time_development', data.total_time_development);
            data.comment += `\n-----------------------------------\nTempo total de desenvolvimento: ${data.total_time_development ?? '-'} hora${data.total_time_development === 1 ? '' : 's'}\nEstimativa de entrega após aprovação: ${data.estimated_delivery_after_approval ?? '-'} ${data.estimated_delivery_after_approval === 1 ? 'mês' : 'meses'}`;
        }

        onSubmit(data.comment, !!data.isPublic, (data.time === '00:00' ? '' : data.time), data.assigned_users, data.attachments, data.transfer_task, data.transfer_task_user_id,
            reminderDate, data.prod_amount, data.prod_amount_problem, data.send_comment_to_approver, data.notify_others_users, data.notify_approver, data.reply_status);
    }

    function getValidationSchema() {
        const validationSchema: FormValidationSchema<CommentFormDialogData> = {
            comment: Yup.string()
                .trim()
                .required()
                .min(3)
                .label('Comentário'),
            time: Yup
                .string()
                .nullable()
                .matches(/^(44:00|([0-3][0-9]|[0-9]|4[0-3]):[0-5][0-9])?$|^$/, 'Tempo inválido. Tempo máximo de 44 horas')
                .label('Registrar tempo')
        }
        return validationSchema;
    }

    var refInputFile = React.createRef<HTMLInputElement>();

    useEffect(() => {
        if (!commentDialogOpen || !enableAttachments || !enablePasteAttachments) return;

        function handlePaste(event: Event) {
            const clipboardItems = (event as ClipboardEvent).clipboardData?.items;
            if (!clipboardItems) return;

            const files = Array.from(clipboardItems)
                .map((item) => item.getAsFile())
                .filter((file): file is File => file !== null);
            if (files.length === 0) return;

            event.preventDefault();
            event.stopPropagation();

            const currentAttachments = pasteAttachmentsRef.current;
            const availableSlots = Math.max(10 - currentAttachments.length, 0);
            if (availableSlots === 0) {
                enqueueSnackbar('O limite de 10 anexos por comentário foi atingido.', { variant: 'warning' });
                return;
            }

            const filesToAdd = files.slice(0, availableSlots).map((file, index) => {
                const extension = file.type.split('/')[1] || 'bin';
                const pastedFile = new File(
                    [file],
                    file.name || `anexo-colado-${Date.now()}-${index}.${extension}`,
                    { type: file.type, lastModified: Date.now() }
                );
                return {
                    id: 0,
                    description: '',
                    name: pastedFile.name,
                    type: attachmentType || AttachmentTaskType.Analysis,
                    file: pastedFile,
                };
            });

            if (files.length > availableSlots) {
                enqueueSnackbar('Só foi possível adicionar anexos até o limite de 10.', { variant: 'warning' });
            }
            void pasteSetFieldValueRef.current?.('attachments', [...currentAttachments, ...filesToAdd]);
        }

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [attachmentType, commentDialogOpen, enableAttachments, enablePasteAttachments, enqueueSnackbar]);

    function inputAttachmentChangeHandler(e: ChangeEvent<HTMLInputElement>, setFieldValue: any, attachments: AttachmentTask[]) {
        const input = e.target;
        if (attachments.length > 9) return;
        if (input?.files && input.files.length > 0) {
            Array.from(input.files).forEach(file => {
                attachments.push({ id: 0, description: '', name: file.name, type: attachmentType || AttachmentTaskType.Analysis, file });
            });
            setFieldValue('attachments', attachments);
        }
    }

    function buttonAttachmentClickHandler(e: ReactMouseEvent<HTMLButtonElement>) {
        const input = refInputFile.current;
        input?.click();
    }

    function removeAttachment(idx: number, setFieldValue: any, attachments: AttachmentTask[]) {
        attachments.splice(idx, 1);
        setFieldValue('attachments', attachments);
    }

    async function openSystemAttachmentDialog(setFieldValue: any, attachments: AttachmentTask[]) {
        try {
            const result = await apiSystemAttachment.getList({ order: { name: 'ASC' }, limit: 15 });
            setSystemAttachments(result);
        } catch (e) {
            enqueueSnackbar('Erro ao carregar anexos do sistema', { variant: 'error' });
            return;
        }
        setSystemAttachmentSearch('');
        setSystemAttachmentSetFieldValue(() => setFieldValue);
        setSystemAttachmentCurrentAttachments(attachments);
        setSystemAttachmentDialogOpen(true);
    }

    async function searchSystemAttachments(search: string) {
        setSystemAttachmentSearch(search);
        try {
            const where: any[] = [];
            if (search.trim()) {
                where.push(['name', 'ilike', `%${search.trim()}%`]);
            }
            const result = await apiSystemAttachment.getList({ where, order: { name: 'ASC' }, limit: 15 });
            setSystemAttachments(result);
        } catch (e) {
            // ignore
        }
    }

    function addSystemAttachment(sa: SystemAttachment) {
        const attachments = [...systemAttachmentCurrentAttachments];
        if (attachments.some(a => a.system_attachment_id === sa.id)) {
            enqueueSnackbar('Este anexo do sistema já foi adicionado', { variant: 'info' });
            return;
        }
        if (attachments.length > 9) {
            enqueueSnackbar('Limite máximo de anexos atingido', { variant: 'warning' });
            return;
        }
        attachments.push({
            id: 0,
            description: '',
            name: '',
            type: attachmentType || AttachmentTaskType.Analysis,
            system_attachment_id: sa.id,
            system_attachment: sa
        });
        systemAttachmentSetFieldValue('attachments', attachments);
        setSystemAttachmentCurrentAttachments(attachments);
        setSystemAttachmentDialogOpen(false);
    }

    async function onClickTextTemplate(textTemplate: TextTemplate, setFieldValue: any, values: CommentFormDialogData) {
        await setFieldValue('comment', textTemplate.template);
        if (textTemplate.assigned_to_users && textTemplate.assigned_to_users.length > 0 && useAssignedToUser) {
            const currentOptions = [...(values.assigned_users_options || [])];
            const currentAssigned = [...(values.assigned_users || [])];

            textTemplate.assigned_to_users.forEach(templateUser => {
                if (!currentAssigned.includes(templateUser.id)) {
                    currentAssigned.push(templateUser.id);
                }
                if (!currentOptions.some(option => option.value === templateUser.id)) {
                    currentOptions.push({ value: templateUser.id, label: templateUser.name });
                }
            });

            await setFieldValue('assigned_users_options', currentOptions);
            await setFieldValue('assigned_users', currentAssigned);
        }
        if (textTemplate.system_attachments && textTemplate.system_attachments.length > 0 && enableAttachments) {
            const currentAttachments = [...values.attachments];
            textTemplate.system_attachments.forEach(sa => {
                if (!currentAttachments.some(a => a.system_attachment_id === sa.id)) {
                    currentAttachments.push({
                        id: 0,
                        description: '',
                        name: sa.name,
                        type: attachmentType || AttachmentTaskType.Analysis,
                        system_attachment_id: sa.id,
                        system_attachment: sa
                    });
                }
            });
            await setFieldValue('attachments', currentAttachments);
        }
    }

    async function onSearchUser(search: string, selectedUsers?: number[]) {
        const where: WhereCondition<User> = [['active', '=', true], ['perm_task', '=', true], ['type', '=', UserType.Commbox], ['name', 'ilike', `%${search}%`]];
        if (!allowSelfAssign) {
            where.push(['id', '!=', user?.id]);
        }
        if (selectedUsers && selectedUsers.length > 0) {
            where.push(['id', 'notIn', selectedUsers]);
        }
        const users = await apiUser.getList({ where, limit: 100, order: { name: 'ASC' }, relations: ['current_or_next_vacation', 'department', 'departments'] });
        if (selectedUsers && selectedUsers.length > 0) {
            const usersAux = await apiUser.getList({ where: [['id', 'in', selectedUsers]], relations: ['current_or_next_vacation', 'department', 'departments'] });
            users.unshift(...usersAux);
        }
        return users.filter((u) => {
            let permsByDp = getUserDepartmentPermissions(u);
            return !!permsByDp.perm_menu_tasks || isInternal;
        }).map((u) => {
            let name = u.name;
            if (showUserVacation) {
                name = getUserWithVacationName(u);
            }
            return { value: u.id, label: name };
        });
    }

    async function applyAutoCommentUsersForSubmit(currentAssigned: number[]): Promise<number[]> {
        const assigned = [...currentAssigned];
        const selectedUsers = currentAssigned.filter((id, idx, arr) => arr.indexOf(id) === idx);

        for (const selectedUserId of selectedUsers) {
            try {
                const u = await apiUser.getById(selectedUserId, {
                    relations: ['department.auto_comment_task_users', 'department.auto_comment_internal_users']
                });
                const autoUsers = (isInternal ? u?.department?.auto_comment_internal_users : u?.department?.auto_comment_task_users) || [];
                autoUsers.forEach((au) => {
                    if (au.id === user?.id) return;
                    if (!assigned.includes(au.id)) {
                        assigned.push(au.id);
                    }
                });
            } catch (e) {
                // ignore
            }
        }

        return assigned;
    }

    const titleId = 'comment-form-dialog-title';
    const dialogTitle = (
        <Box
            id={titleId}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
                cursor: isDragging ? 'grabbing' : 'move',
                userSelect: 'none',
                background: isDark2
                    ? 'linear-gradient(135deg, #4a5055 0%, #343a3e 100%)'
                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                mx: -3,
                mt: -2,
                mb: 2,
                px: 2,
                py: 1.5,
                borderRadius: '8px 8px 0 0'
            }}
            onMouseDown={handleDragStart}
        >
            <Box display="flex" alignItems="center" gap={1.5}>
                <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.8 }} />
                <Typography variant="subtitle1" fontWeight={600} letterSpacing={0.3}>Novo Comentário</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        setIsMinimized((prev) => !prev);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    sx={{
                        color: 'white',
                        opacity: 0.85,
                        '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.15)' }
                    }}
                >
                    {isMinimized ? <OpenInFullIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
                </IconButton>
                <IconButton
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        onCancel();
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    sx={{
                        color: 'white',
                        opacity: 0.85,
                        '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.15)' }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );

    function getHoursRecordedToday(user: User): string {
        const recordedMinutes = Number(user?.time_recorded_today ?? 0);
        const hasRecordedToday = isSameDay(user?.last_time_recorded_today_at);
        if (!hasRecordedToday || recordedMinutes <= 0) {
            return '00:00';
        }
        const hours = Math.floor(recordedMinutes / 60);
        const minutes = recordedMinutes % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    return (
        <>
            <FormDialog<CommentFormDialogData>
                show={commentDialogOpen}
                title={dialogTitle}
                dismiss={() => null}
                onSubmit={async (values, dirty, errors) => {
                    if (errors) {
                        for (var error of Object.values(errors).filter(e => !!e)) {
                            enqueueSnackbar(error, { variant: 'error' });
                        }
                        return;
                    }
                    const { comment, isPublic, time, assigned_users, attachments, transfer_task, transfer_task_user_id, add_reminder, reminder_date, reminder_time,
                        prod_amount, prod_amount_problem, send_comment_to_approver, total_time_development, estimated_delivery_after_approval, notify_others_users,
                        notify_approver, reply_status } = values;

                    if (useTime || useAssignedToUser) {
                        if (!time && !assigned_users.length) {
                            enqueueSnackbar('Informe o tempo ou o usuário atribuído', { variant: 'error' });
                            return;
                        }
                    }

                    let assignedUsersForSubmit = assigned_users;
                    if (useAssignedToUser && assigned_users.length > 0) {
                        assignedUsersForSubmit = await applyAutoCommentUsersForSubmit(assigned_users);
                    }

                    onSubmitHandler({
                        comment, isPublic, time, assigned_users: assignedUsersForSubmit, attachments, transfer_task, transfer_task_user_id, add_reminder, reminder_date,
                        reminder_time, prod_amount, prod_amount_problem, send_comment_to_approver, total_time_development, estimated_delivery_after_approval, notify_others_users,
                        notify_approver, reply_status
                    });
                }}
                initialValues={{
                    comment: '',
                    isPublic: allowPublicComment ? null : !!onlyPublicComments,
                    time: '',
                    assigned_users: replyContext?.recipients.map((recipient) => recipient.id) || [],
                    assigned_users_options: replyContext?.recipients.map((recipient) => ({ value: recipient.id, label: recipient.name })) || [],
                    attachments: [],
                    transfer_task: false,
                    transfer_task_user_id: null,
                    add_reminder: false,
                    reminder_date: null,
                    reminder_time: '',
                    prod_amount: null,
                    prod_amount_problem: null,
                    send_comment_to_approver: false,
                    total_time_development: null,
                    estimated_delivery_after_approval: null,
                    notify_others_users: [],
                    notify_approver: false,
                    reply_status: 'keepUnresolved'
                }}
                enableReinitialize={true}
                postValidation={(values) => {
                    return validateSchema(values, getValidationSchema());
                }}
                maxWidth='lg'
                fullWidth
                dialogStyle={{
                    overflowY: 'auto',
                    position: 'fixed',
                    top: dialogPosition.top,
                    left: dialogPosition.left,
                    margin: 0,
                    transform: 'none',
                    minWidth: 480,
                    cursor: isDragging ? 'grabbing' : 'default',
                    display: isMinimized ? 'none' : undefined,
                    borderRadius: '12px',
                    boxShadow: isDark2
                        ? '0 12px 36px rgba(0,0,0,0.42), 0 2px 10px rgba(0,0,0,0.24)'
                        : '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                    border: isDark2 ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(0,0,0,0.08)'
                }}
                dialogRootStyle={{
                    pointerEvents: isMinimized ? 'none' : 'auto',
                    display: isMinimized ? 'none' : 'block',
                }}
                dialogTitleStyle={{
                    padding: '8px 12px'
                }}
                hideBackdrop={isMinimized}
                disableScrollLock={isMinimized}
                paperRef={paperRef}
            >
                {({ values, errors, handleChange, setFieldValue, isSubmitting, dirty, handleChangeNumber }) => {
                    pasteSetFieldValueRef.current = setFieldValue;
                    pasteAttachmentsRef.current = values.attachments;
                    return (
                    <Collapse in={!isMinimized} unmountOnExit={false} sx={{ overflow: 'hidden' }}>
                        <Box sx={{
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`
                        }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Mensagem
                            </Typography>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item xs={12}>
                                    <SafeDeskTextEditor
                                        value={values.comment}
                                        onChange={(e) => {
                                            setFieldValue('comment', e.target.value)
                                        }}
                                        style={{ resize: 'vertical', overflow: 'auto', height: 150, minHeight: 150, maxHeight: 400 }}
                                        placeholder='Digite seu comentário aqui...'
                                    />
                                    {showTextTemplates && page ? <TextTemplateButtons task={task} page={page} disabled={!!values.comment} style={{ marginTop: 8 }} type={TextTemplateType.Comment} onClick={(tt) => onClickTextTemplate(tt, setFieldValue, values)} /> : null}
                                </Grid>
                            </Grid>
                        </Box>
                        {(allowPublicComment || useTime) && <Box sx={{
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`
                        }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Configurações
                            </Typography>
                            <Grid container alignItems="center" spacing={2}>
                                {allowPublicComment ? <>
                                    <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                                        <SelectSearch<boolean>
                                            name='isPublic'
                                            label='Visibilidade'
                                            options={[
                                                { label: 'Público', value: true },
                                                { label: 'Interno', value: false },
                                            ]}
                                            value={values.isPublic}
                                            onChangeHandle={async (_, v) => {
                                                await setFieldValue('isPublic', v || false);
                                                await setFieldValue('notify_approver', false);
                                            }}
                                            margin='dense'
                                            fullWidth
                                            required
                                        />
                                    </Grid>
                                    {(ticketTypeApprovers || []).length > 0 && values.isPublic ? <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                                        <SelectSearch<boolean>
                                            name='notify_approver'
                                            label='Notificar aprovadores?'
                                            options={[
                                                { label: 'Sim', value: true },
                                                { label: 'Não', value: false },
                                            ]}
                                            value={values.notify_approver}
                                            onChangeHandle={async (_, v) => {
                                                await setFieldValue('notify_approver', v || false);
                                            }}
                                            margin='dense'
                                            fullWidth
                                            required
                                        />
                                    </Grid> : null}
                                </> : null}
                                {useTime ? <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
                                    <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} gap={0.5}>
                                        <MaskedInput
                                            mask={[/\d/, /\d/, ':', /[0-5]/, /\d/]}
                                            name='time'
                                            value={values.time}
                                            onChange={(e) => {
                                                setFieldValue('time', e.target.value)
                                            }}
                                            render={(ref, props) => (
                                                <TextField
                                                    inputRef={ref}
                                                    {...props}
                                                    value={values.time}
                                                    label={'Tempo (HH:MM)'}
                                                    margin="dense"
                                                    variant="outlined"
                                                    size="small"
                                                    error={!!errors.time && !!dirty.time}
                                                    helperText={!!errors.time && !!dirty.time && errors.time}
                                                    sx={{ width: 130, mr: 1 }}
                                                />
                                            )}
                                        />
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {['00:05', '00:30', '01:00', '02:00', '03:48', '04:00', '05:00', '08:48'].map((time) => (
                                                <Button
                                                    key={time}
                                                    onClick={() => setFieldValue('time', time)}
                                                    variant={'contained'}
                                                    size='small'
                                                    sx={{
                                                        minWidth: 52,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        borderRadius: 1.5,
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    {time}
                                                </Button>
                                            ))}
                                        </Box>
                                        {loadedUser && (
                                            <Box sx={{
                                                ml: 1,
                                                px: 1.5,
                                                py: 0.5,
                                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                <Typography variant="caption" color="text.secondary">Hoje:</Typography>
                                                <Typography variant="caption" fontWeight={600} color="primary">{getHoursRecordedToday(loadedUser)}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid> : null}
                            </Grid>
                        </Box>}
                        {useAssignedToUser ? <Box sx={{
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`
                        }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Atribuição
                            </Typography>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item xs={12}>
                                    <SelectSearchMultiAsync<number>
                                        placeholder="Selecione os usuários..."
                                        label="Atribuído para"
                                        loadOptions={(s, ss) => onSearchUser(s, ss?.map(as => as.value) || values.assigned_users)}
                                        value={values.assigned_users_options || []}
                                        onChangeHandle={async (_, value) => {
                                            const nextAssigned = value.map(v => v.value);
                                            await setFieldValue('assigned_users_options', value);
                                            await setFieldValue('assigned_users', nextAssigned);
                                            if (!values.transfer_task_user_id && nextAssigned.length > 0) {
                                                await setFieldValue('transfer_task_user_id', nextAssigned[0]);
                                            }
                                        }}
                                        name='assigned_users'
                                        defaultOptions={userList}
                                        fullWidth
                                        margin='dense'
                                    />
                                </Grid>
                                {relatedUsers && relatedUsers.length > 0 && (
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Sugestões:</Typography>
                                            {relatedUsers.map((u, ii) => {
                                                let name = u.name;
                                                if (showUserVacation) {
                                                    name = getUserWithVacationName(u);
                                                }
                                                const isSelected = values.assigned_users.includes(u.id);
                                                return (
                                                    <Button
                                                        key={`related-user-${u.id}`}
                                                        size="small"
                                                        variant={'contained'}
                                                        disabled={isSelected}
                                                        onClick={async () => {
                                                            if (isSelected) return;
                                                            const nextOptions: OptionItem<number>[] = [...(values.assigned_users_options || []), { value: u.id, label: name }];
                                                            const nextAssigned: number[] = [...values.assigned_users, u.id];
                                                            await setFieldValue('assigned_users_options', nextOptions);
                                                            await setFieldValue('assigned_users', nextAssigned);
                                                            if (!values.transfer_task_user_id) {
                                                                await setFieldValue('transfer_task_user_id', u.id);
                                                            }
                                                        }}
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            py: 0.25,
                                                            px: 1,
                                                            borderRadius: 1,
                                                            textTransform: 'none'
                                                        }}
                                                    >
                                                        {name}
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                            {user?.id === task?.user_id && (
                                <Box sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: (theme) => `1px dashed ${theme.palette.divider}`,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        componentsProps={{ typography: { fontSize: '0.8rem' } }}
                                        control={
                                            <Checkbox
                                                name='transfer_task'
                                                onChange={async (_, v) => {
                                                    await setFieldValue('transfer_task', v);
                                                }}
                                                checked={values.transfer_task}
                                                size="small"
                                                sx={{ p: '4px 6px' }}
                                            />
                                        }
                                        label="Transferir chamado para"
                                    />
                                    <SelectSearch<number>
                                        name='transfer_task_user_id'
                                        label=''
                                        options={userList}
                                        value={values.transfer_task_user_id}
                                        onChangeHandle={async (_, v) => {
                                            await setFieldValue('transfer_task_user_id', v);
                                        }}
                                        margin='dense'
                                        style={{ flex: 1, marginLeft: 8 }}
                                        inputStyle={{ width: '100%' }}
                                        required={values.transfer_task}
                                        isDisabled={!values.transfer_task}
                                    />
                                </Box>
                            )}
                        </Box> : null}
                        {replyContext && !replyContext.isResolved ? <Box sx={{
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`
                        }}>
                            <FormControl component="fieldset">
                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Comentário respondido aqui
                                </Typography>
                                <RadioGroup
                                    row
                                    value={values.reply_status}
                                    onChange={(event) => setFieldValue('reply_status', event.target.value as CommentReplyStatus)}
                                >
                                    <FormControlLabel value="keepUnresolved" control={<Radio size="small" />} label={`Manter não ${replyContext.isAwared ? '' : 'ciente/'}resolvido`} />
                                    {!replyContext.isAwared && <FormControlLabel value="markAwared" control={<Radio size="small" />} label="Marcar ciente mas não resolvido" />}
                                    <FormControlLabel value="markResolved" control={<Radio size="small" />} label="Marcar como resolvido" />
                                </RadioGroup>
                            </FormControl>
                        </Box> : null}
                        {enableAttachments === true ? (
                            <Box sx={{
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                border: (theme) => `1px dashed ${theme.palette.divider}`
                            }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={values.attachments.length > 0 ? 1.5 : 0}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Anexos {values.attachments.length > 0 && `(${values.attachments.length})`}
                                    </Typography>
                                    <Box display="flex" gap={1}>
                                        <Button
                                            onClick={buttonAttachmentClickHandler}
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.75rem' }}
                                        >
                                            <input
                                                ref={refInputFile}
                                                type='file'
                                                style={{ display: 'none' }}
                                                onChange={(e) => inputAttachmentChangeHandler(e, setFieldValue, values.attachments)}
                                                multiple={true}
                                                accept={"*"}
                                                id={"comment_attachment"}
                                            />
                                            + Adicionar arquivo
                                        </Button>
                                        {enableSystemAttachments && <Button
                                            onClick={() => openSystemAttachmentDialog(setFieldValue, values.attachments)}
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AttachFileIcon fontSize="small" />}
                                            sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.75rem' }}
                                        >
                                            Anexo do sistema
                                        </Button>}
                                    </Box>
                                </Box>
                                {values.attachments.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {values.attachments.map((at, idx) => (
                                            <AttachmentPreviewBox
                                                file={at.file}
                                                id={at.task_id || null}
                                                model="Task"
                                                name={at.name}
                                                key={`comment-attachments-${idx}`}
                                                onDelete={() => removeAttachment(idx, setFieldValue, values.attachments)}
                                                systemAttachmentId={at.system_attachment_id}
                                                systemAttachmentFile={at.system_attachment?.file}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ) : null}
                        {enableReminder && (
                            <Box sx={{
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                border: (theme) => `1px solid ${theme.palette.divider}`
                            }}>
                                <Box display="flex" alignItems="center" mb={values.add_reminder ? 1.5 : 0}>
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={
                                            <Checkbox
                                                name='add_reminder'
                                                onChange={async (_, v) => {
                                                    await setFieldValue('add_reminder', v);
                                                    await setFieldValue('reminder_date', null);
                                                    if (!v) {
                                                        await setFieldValue('reminder_time', '');
                                                    } else {
                                                        await setFieldValue('reminder_time', '00:00');
                                                    }
                                                }}
                                                checked={values.add_reminder}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" fontWeight={500}>
                                                Adicionar lembrete
                                            </Typography>
                                        }
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        (para mim e mencionados)
                                    </Typography>
                                </Box>
                                {values.add_reminder && (
                                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                                        <DatePicker
                                            label={'Data'}
                                            value={values.reminder_date}
                                            onChange={(value) => setFieldValue('reminder_date', value)}
                                            required={values.add_reminder}
                                            style={{ width: '140px' }}
                                            disabled={!values.add_reminder}
                                        />
                                        <TimePicker
                                            label={'Horário'}
                                            value={values.reminder_time}
                                            onChange={(value) => setFieldValue('reminder_time', value)}
                                            required={values.add_reminder}
                                            name='reminder_time'
                                            style={{ width: '90px' }}
                                            disabled={!values.add_reminder}
                                        />
                                        <Box display="flex" gap={1}>
                                            {[3, 7, 14, 30].map((days) => (
                                                <Button
                                                    key={days}
                                                    onClick={() => setFieldValue('reminder_date', fns.addDays(new Date(), days))}
                                                    variant="contained"
                                                    size='small'
                                                    disabled={!values.add_reminder}
                                                    sx={{
                                                        minWidth: 52,
                                                        fontSize: '0.7rem',
                                                        borderRadius: 1.5,
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    {fns.format(fns.addDays(new Date(), days), 'dd/MM')}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {page === 'taskInternal' && (ruleProdAmount === TicketLayoutTypeRule.Optional || ruleProdAmount === TicketLayoutTypeRule.Required) ?
                            <Box sx={{
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                border: (theme) => `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Produção
                                </Typography>
                                <Grid container alignItems="center" spacing={1} marginTop={2}>
                                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                        <TextField
                                            name="prod_amount"
                                            label={'Quantidade produzida'}
                                            margin="dense"
                                            variant="outlined"
                                            fullWidth
                                            inputProps={{ maxLength: 6 }}
                                            value={values.prod_amount || ''}
                                            onChange={e => {
                                                const re = /^[0-9\b]+$/;
                                                if (e.target.value === '' || re.test(e.target.value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            required={ruleProdAmount === TicketLayoutTypeRule.Required}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                        <TextField
                                            name="prod_amount_problem"
                                            label={'Quantidade com problema'}
                                            margin="dense"
                                            variant="outlined"
                                            fullWidth
                                            inputProps={{ maxLength: 6 }}
                                            value={values.prod_amount_problem || ''}
                                            onChange={e => {
                                                const re = /^[0-9\b]+$/;
                                                if (e.target.value === '' || re.test(e.target.value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            required={ruleProdAmount === TicketLayoutTypeRule.Required}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                            : null}
                        {(ticketTypeApprovers || []).length > 0 && values.isPublic && !!user?.perm_add_comment_approver ? (
                            <Box sx={{
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                border: (theme) => `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Aprovação
                                </Typography>
                                <Grid container alignItems="center" spacing={1} marginTop={2}>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                        <SelectSearch<boolean>
                                            name='send_comment_to_approver'
                                            label='Comentário para aprovadores?'
                                            options={[
                                                { label: 'Sim', value: true },
                                                { label: 'Não', value: false },
                                            ]}
                                            value={values.send_comment_to_approver}
                                            onChangeHandle={async (_, v) => {
                                                await setFieldValue('send_comment_to_approver', v || false);
                                                if (!v) {
                                                    await setFieldValue('total_time_development', null);
                                                    await setFieldValue('estimated_delivery_after_approval', null);
                                                    await setFieldValue('notify_others_users_options', []);
                                                    await setFieldValue('notify_others_users', []);
                                                }
                                            }}
                                            margin='dense'
                                            fullWidth
                                            required
                                            helperText={values.send_comment_to_approver && <>O comentário será enviado para os aprovadores: {(ticketTypeApprovers || []).map((a) => a.name).join(', ')}</>}
                                        />
                                    </Grid>
                                    {values.send_comment_to_approver && <>
                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                            <SelectSearchMultiAsync<number>
                                                placeholder="Notificar por email também outros usuários"
                                                label="Notificar por email também outros usuários"
                                                loadOptions={(s, ss) => onSearchUser(s, ss?.map(as => as.value) || values.notify_others_users)}
                                                value={values.notify_others_users_options || []}
                                                onChangeHandle={async (_, value) => {
                                                    await setFieldValue('notify_others_users_options', value);
                                                    await setFieldValue('notify_others_users', value.map(v => v.value));
                                                }}
                                                name='notify_others_users'
                                                defaultOptions={userList}
                                                fullWidth
                                                margin='dense'
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                            <TextField
                                                value={values.total_time_development || ''}
                                                label={'Tempo total de desenvolvimento (em horas)'}
                                                margin="dense"
                                                variant="outlined"
                                                name='total_time_development'
                                                fullWidth
                                                required
                                                error={!!errors.total_time_development && !!dirty.total_time_development}
                                                helperText={!!errors.total_time_development && !!dirty.total_time_development && errors.total_time_development}
                                                inputProps={{ maxLength: 6 }}
                                                onChange={e => {
                                                    const re = /^[0-9\b]+$/;
                                                    if (e.target.value === '' || re.test(e.target.value)) {
                                                        handleChangeNumber(e);
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                            <TextField
                                                value={values.estimated_delivery_after_approval || ''}
                                                label={'Estimativa de entrega após a aprovação (em meses)'}
                                                margin="dense"
                                                variant="outlined"
                                                name='estimated_delivery_after_approval'
                                                fullWidth
                                                required
                                                error={!!errors.estimated_delivery_after_approval && !!dirty.estimated_delivery_after_approval}
                                                helperText={!!errors.estimated_delivery_after_approval && !!dirty.estimated_delivery_after_approval && errors.estimated_delivery_after_approval}
                                                inputProps={{ maxLength: 6 }}
                                                onChange={e => {
                                                    const re = /^[0-9\b]+$/;
                                                    if (e.target.value === '' || re.test(e.target.value)) {
                                                        handleChangeNumber(e);
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </>}
                                </Grid>
                            </Box>
                        ) : null}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1.5,
                            pt: 2,
                            mt: 1,
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`
                        }}>
                            <Button
                                size='small'
                                variant="contained"
                                color="error"
                                disabled={isSubmitting}
                                onClick={() => (dirty && Object.getOwnPropertyNames(dirty).length > 0) ? confirmation(undefined, {
                                    title: 'Quer realmente cancelar o comentário?',
                                    okLabel: 'Sim',
                                    cancelLabel: 'Não',
                                    onProceed: () => {
                                        onCancel()
                                    }
                                }) : onCancel()}
                            >
                                Cancelar
                            </Button>
                            <Button
                                size='small'
                                type='submit'
                                variant="contained"
                                disabled={isSubmitting}
                            >
                                Enviar Comentário
                            </Button>
                        </Box>
                    </Collapse>
                    );
                }}
            </FormDialog >
            <Dialog
                open={systemAttachmentDialogOpen}
                maxWidth="sm"
                fullWidth
                onClose={(ev, reason) => {
                    if (reason === 'backdropClick') return;
                    setSystemAttachmentDialogOpen(false)
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>Selecionar anexo do sistema</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ px: 2, pt: 1.5, pb: 1, position: 'sticky', top: 0, zIndex: 1 }}>
                        <TextField
                            placeholder="Buscar por nome..."
                            size="small"
                            fullWidth
                            value={systemAttachmentSearch}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSystemAttachmentSearch(value);
                                if (systemAttachmentSearchTimerRef.current) clearTimeout(systemAttachmentSearchTimerRef.current);
                                systemAttachmentSearchTimerRef.current = setTimeout(() => searchSystemAttachments(value), 300);
                            }}
                            autoFocus
                        />
                    </Box>
                    {systemAttachments.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            {systemAttachmentSearch.trim() ? 'Nenhum anexo encontrado.' : 'Nenhum anexo do sistema cadastrado.'}
                        </Typography>
                    ) : (
                        <List dense sx={{ px: 1 }}>
                            {systemAttachments.map((sa) => {
                                const alreadyAdded = systemAttachmentCurrentAttachments.some(a => a.system_attachment_id === sa.id);
                                return (
                                    <ListItem key={sa.id} disablePadding sx={{ mb: 0.5 }}>
                                        <ListItemButton
                                            onClick={() => addSystemAttachment(sa)}
                                            disabled={alreadyAdded}
                                            sx={{ borderRadius: 1, py: 0.75 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <AttachFileIcon fontSize="small" color={alreadyAdded ? 'disabled' : 'primary'} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={sa.name}
                                                secondary={alreadyAdded ? 'Já adicionado' : sa.file + (sa.file_size ? ` (${convertFileByteToSizeString(sa.file_size)})` : '')}
                                                primaryTypographyProps={{ noWrap: true, title: sa.name, color: alreadyAdded ? 'text.disabled' : 'text.primary' }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <ErrorButton size='small' variant='contained' onClick={() => setSystemAttachmentDialogOpen(false)}>Fechar</ErrorButton>
                </DialogActions>
            </Dialog>
            {isMinimized && (
                <Box
                    position="fixed"
                    bottom={24}
                    right={24}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    padding={1.5}
                    paddingLeft={2.5}
                    borderRadius={3}
                    boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                    sx={{
                        bgcolor: (theme) => theme.palette.primary.main,
                        color: 'white',
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.5)', animation: 'pulse 2s infinite' }} />
                    <Typography variant='body2' fontWeight={500}>Comentário em andamento</Typography>
                    <Button
                        size='small'
                        variant='contained'
                        onClick={() => setIsMinimized(false)}
                        sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.3)'
                            }
                        }}
                    >
                        Restaurar
                    </Button>
                    <IconButton
                        size='small'
                        onClick={() => {
                            onCancel();
                            setIsMinimized(false);
                        }}
                        sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
                    >
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </Box>
            )}
        </>
    );
}